import { useEffect, useRef } from 'react';

interface CalendlyEmbedProps {
  url: string;
  prefill?: {
    email?: string;
    name?: string;
  };
  onEventScheduled?: (event: any) => void;
  className?: string;
}

declare global {
  interface Window {
    Calendly: {
      initInlineWidget: (options: {
        url: string;
        parentElement: HTMLElement;
        prefill?: {
          email?: string;
          name?: string;
        };
      }) => void;
    };
  }
}

const CalendlyEmbed: React.FC<CalendlyEmbedProps> = ({
  url,
  prefill,
  onEventScheduled,
  className = ''
}) => {
  const calendlyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Build URL with query parameters
    const urlObj = new URL(url);
    
    // Add hide options
    urlObj.searchParams.set('hide_landing_page_details', '1');
    urlObj.searchParams.set('hide_gdpr_banner', '1');
    
    // Add prefill parameters if provided
    if (prefill?.email) {
      urlObj.searchParams.set('email', prefill.email);
    }
    if (prefill?.name) {
      urlObj.searchParams.set('name', prefill.name);
    }

    const fullUrl = urlObj.toString();

    // Set data-url attribute for Calendly to auto-initialize
    if (calendlyRef.current) {
      calendlyRef.current.setAttribute('data-url', fullUrl);
    }

    // Load Calendly script if not already loaded
    const existingScript = document.querySelector(
      'script[src="https://assets.calendly.com/assets/external/widget.js"]'
    );

    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.body.appendChild(script);
    }

    // Listen for Calendly events
    const handleMessage = (e: MessageEvent) => {
      // Calendly sends events with 'calendly.' prefix
      if (
        e.data?.event &&
        typeof e.data.event === 'string' &&
        e.data.event.startsWith('calendly.')
      ) {
        if (
          e.data.event === 'calendly.event_scheduled' &&
          onEventScheduled
        ) {
          // Pass the entire event data structure
          onEventScheduled(e.data);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      // Clean up data-url when component unmounts
      if (calendlyRef.current) {
        calendlyRef.current.removeAttribute('data-url');
        calendlyRef.current.innerHTML = '';
      }
    };
  }, [url, prefill, onEventScheduled]);

  return (
    <div
      ref={calendlyRef}
      className={`calendly-inline-widget ${className}`}
      style={{ minWidth: '320px', height: '700px' }}
    />
  );
};

export default CalendlyEmbed;

